/**
 * Tiny seeded RNG (mulberry32). Deterministic on purpose: the server seeds every
 * game, so a shuffle can be replayed in a test and both players always agree on
 * the same board.
 */
export interface Rng {
	/** Float in [0, 1). */
	next(): number;
	/** Integer in [0, maxExclusive). */
	int(maxExclusive: number): number;
	/** Fisher-Yates copy — does not mutate the input. */
	shuffle<T>(items: readonly T[]): T[];
	/** `count` distinct items picked at random. */
	sample<T>(items: readonly T[], count: number): T[];
}

/**
 * xoshiro128** — 128 bits of state, which is the part that matters here.
 *
 * The state size is a security property, not a quality one. Every hidden thing
 * in this platform is dealt from a seeded generator: ship positions, mine
 * locations, the secret code, the shuffled deck. With a 32-bit seed an opponent
 * could search the whole keyspace offline in seconds, match it against the one
 * fleet they *are* allowed to see, and read the other off the same stream. At
 * 128 bits that search does not exist.
 */
function xoshiro(a: number, b: number, c: number, d: number): Rng {
	let [s0, s1, s2, s3] = [a >>> 0, b >>> 0, c >>> 0, d >>> 0];

	const next = (): number => {
		const rotated = ((Math.imul(s1, 5) << 7) | (Math.imul(s1, 5) >>> 25)) >>> 0;
		const result = Math.imul(rotated, 9) >>> 0;

		const t = (s1 << 9) >>> 0;
		s2 = (s2 ^ s0) >>> 0;
		s3 = (s3 ^ s1) >>> 0;
		s1 = (s1 ^ s2) >>> 0;
		s0 = (s0 ^ s3) >>> 0;
		s2 = (s2 ^ t) >>> 0;
		s3 = ((s3 << 11) | (s3 >>> 21)) >>> 0;

		return result / 4294967296;
	};

	const int = (maxExclusive: number): number => Math.floor(next() * maxExclusive);

	const shuffle = <T>(items: readonly T[]): T[] => {
		const copy = [...items];
		for (let i = copy.length - 1; i > 0; i--) {
			const j = int(i + 1);
			const a = copy[i];
			const b = copy[j];
			copy[i] = b;
			copy[j] = a;
		}
		return copy;
	};

	const sample = <T>(items: readonly T[], count: number): T[] =>
		shuffle(items).slice(0, Math.min(count, items.length));

	return { next, int, shuffle, sample };
}

/**
 * A generator from a single number. Deterministic, so a shuffle can be replayed
 * in a test — which is the only thing this is for. Anything a player must not
 * be able to predict wants `createSecureRng` instead.
 */
export function createRng(seed: number): Rng {
	// splitmix32, purely to spread one number across the whole state: seeding
	// three of the four words with zero makes the first outputs correlate.
	let state = seed >>> 0;
	const spread = (): number => {
		state = (state + 0x9e3779b9) >>> 0;
		let z = state;
		z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
		z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
		return (z ^ (z >>> 15)) >>> 0;
	};
	return xoshiro(spread(), spread(), spread(), spread());
}

/**
 * `crypto.getRandomValues` is present in every environment this package runs in
 * — browsers, workerd and Node — but core compiles against ES2022 alone, with
 * no DOM or platform library, so the one function it borrows is declared here
 * rather than pulling a whole environment into a package of pure game rules.
 */
declare const crypto: { getRandomValues<T extends Uint32Array>(array: T): T };

/**
 * A generator nobody can predict or reconstruct. This is what deals a real
 * game: the whole of the hidden state depends on it.
 */
export function createSecureRng(): Rng {
	const words = crypto.getRandomValues(new Uint32Array(4));
	return xoshiro(words[0], words[1], words[2], words[3]);
}
